// Paid Growth - job cards (step-by-step instructions), keyed by job code.
// A step is { text, tag }; a red STOP bar is { stop }. Loaded automatically by constants/jobCards.js.

export const division = "Paid Growth";

export const cards = {
  P1: {
    title: "Set up a new Meta ad account",
    before: [
      "Client has accepted our access request",
      "Signed scope and a named person who can approve budgets",
    ],
    steps: [
      { text: "Ask for partner access. NEVER use the client's login", tag: "Access" },
      { text: "Check the pixel is installed and actually firing", tag: "Pixel live" },
      { text: "Set up CAPI as the second, more reliable route for the same data", tag: "CAPI live" },
      { text: "Set up the conversion events and mark which ONE is the main one", tag: "Events set" },
      { text: "Write down HOW the account is funded: automatic billing, or someone tops it up manually", tag: "Funding noted" },
      { text: "If it is manual, write down WHO tops it up and at what point we tell them", tag: "Top-up plan" },
      { text: "Check the spend cap is set, and write down what it is", tag: "Cap confirmed" },
      { text: "Name everything our way so reports make sense later", tag: "Naming done" },
      { text: "Run the setup checklist and save it", tag: "Checklist" },
      { stop: "Division Lead or named project lead signs off the account as ready" },
    ],
    finishedWhen: "Account is ready, funding and cap are written down, and the checklist is saved.",
  },
  P2: {
    title: "Set up a new Google Ads account",
    before: [
      "Client has accepted our access request",
      "Signed scope and a named budget approver",
    ],
    steps: [
      { text: "Link the account to our manager account", tag: "Linked" },
      { text: "Install the tags through the tag manager, not directly on the site", tag: "Tags live" },
      { text: "Set up conversion actions and decide which ones Google should optimise for", tag: "Conversions set" },
      { text: "Upload the basic negative keyword lists BEFORE any spend", tag: "Negatives live" },
      { text: "Write down how the account is funded and who tops it up", tag: "Funding noted" },
      { text: "Check the spend cap and the billing details", tag: "Cap + billing" },
      { text: "Name everything our way", tag: "Naming done" },
      { text: "Run the setup checklist and save it", tag: "Checklist" },
      { stop: "Division Lead or named project lead signs off the account as ready" },
    ],
    finishedWhen: "Account is ready, negatives are live, funding and cap are written down.",
  },
  P3: {
    title: "Prove the tracking works — BEFORE any spend",
    before: [
      "Account set up",
      "Access to the platform, analytics and the client's CRM",
    ],
    steps: [
      { text: "Agree in writing what counts as a result for this client", tag: "Conversion list" },
      { text: "Make sure the same action is called the same thing everywhere", tag: "Names match" },
      { text: "Submit a REAL test lead yourself. Use a real email you can check", tag: "Test submitted" },
      { text: "Check it appeared in the ad platform", tag: "Platform ok" },
      { text: "Check it appeared in analytics", tag: "Analytics ok" },
      { text: "Check it appeared in the CLIENT'S CRM. This is the one people skip", tag: "CRM ok" },
      { text: "Screenshot all three and save them", tag: "Evidence saved" },
      { text: "Check the same lead is not being counted twice", tag: "No double count" },
      { text: "Put the UTM tags on every link, our way", tag: "UTMs done" },
      { text: "Book a reminder to test this again every month", tag: "Monthly check set" },
    ],
    finishedWhen: "A real test lead reached all three places, with screenshots saved. NO SPEND before this.",
  },
  P4: {
    title: "Launch a campaign",
    before: [
      "Tracking proved with screenshots (card P3)",
      "Approved plan and budget",
      "Creative received from the social team",
    ],
    steps: [
      { text: "Check the goal and budget match the approved plan", tag: "Matches plan" },
      { text: "Check the creative matches what you briefed", tag: "Creative ok" },
      { text: "Build the targeting or the keyword structure", tag: "Built" },
      { text: "Add the exclusions. On lead campaigns, stop paying for people who already filled the form", tag: "Exclusions set" },
      { text: "Put UTM tags on every link", tag: "UTMs done" },
      { text: "Check there is enough money in the account for the first 2 weeks", tag: "Funding ok" },
      { text: "Check the landing page is LIVE and the social team has confirmed it passed their checks", tag: "Page confirmed" },
      { text: "TWO people check before launch: you, and one other person", tag: "Two-person check" },
      { stop: "Division Lead or named project lead approves the launch" },
      { text: "Launch", tag: "Live" },
      { text: "Check it again 24 hours later", tag: "24h audit" },
      { text: "On Google, look at what people actually searched after 48 hours", tag: "Search terms" },
    ],
    finishedWhen: "Campaign is live, two people checked it, and the 24 hour audit is done.",
  },
  P5: {
    title: "Every day — 20 minutes",
    before: [
      "Live campaigns",
    ],
    steps: [
      { text: "FIRST: how many days of money are left in the account?", tag: "Runway known" },
      { text: "If it is running low, tell the client TODAY. Do not wait", tag: "Client told" },
      { text: "Is spend ahead of or behind plan for the month?", tag: "Pacing checked" },
      { text: "What is a lead costing today compared to target?", tag: "Cost checked" },
      { text: "Are the ads actually being shown?", tag: "Delivery ok" },
      { text: "Has anything been rejected by the platform?", tag: "No rejections" },
      { text: "Are the landing pages still working?", tag: "Pages live" },
      { text: "Is the tracking still firing?", tag: "Tracking ok" },
      { text: "LAST AND MOST IMPORTANT: are leads actually arriving in the client's CRM?", tag: "Leads arriving" },
    ],
    finishedWhen: "All nine checked. If any one fails, go to card P9 now.",
  },
  P6: {
    title: "Every week — improving it",
    before: [
      "At least a week of data",
      "The change log",
    ],
    steps: [
      { text: "Google: read what people actually searched. Add the bad ones as negatives", tag: "Negatives added" },
      { text: "Check where the ads are appearing and cut the places that waste money", tag: "Placements cleaned" },
      { text: "Move budget towards what is working", tag: "Budget moved" },
      { text: "Check if the same people are seeing the ad too many times", tag: "Fatigue checked" },
      { text: "Decide if the creative needs refreshing, and tell the social team EARLY", tag: "Refresh raised" },
      { text: "Write down EVERY change: what you changed, when, and WHY", tag: "Change log" },
    ],
    finishedWhen: "Changes made and logged with a reason. Without the reason, nobody can explain what moved the numbers.",
  },
  P7: {
    title: "Ask for creative",
    before: [
      "Approved plan",
      "You know what you want to test",
    ],
    steps: [
      { text: "Decide the angle: what are we saying, to whom, and what are we testing", tag: "Angle" },
      { text: "Write the brief: sizes, how many versions, the offer, the deadline", tag: "Brief" },
      { text: "Say clearly what is being TESTED. Without this they cannot build useful versions", tag: "Test stated" },
      { text: "Send it through your Division Lead, at least 5 working days before launch", tag: "Sent" },
      { text: "Video ads go to Design and Video, not the social team: 5 working days for an edit, 2 weeks for a shoot", tag: "Right team" },
      { text: "We brief. We do not design, write or edit. That is their job", tag: "Boundary clear" },
      { text: "Every month, tell them which creatives worked and why", tag: "Feedback sent" },
    ],
    finishedWhen: "Brief sent with the right notice, and last month's feedback has gone back to them.",
  },
  P8: {
    title: "Testing — one thing at a time",
    before: [
      "A campaign running with enough budget to learn something",
    ],
    steps: [
      { text: "Write down what you think will happen and why, BEFORE you start", tag: "Hypothesis" },
      { text: "Change only ONE thing. Change three and you learn nothing", tag: "One variable" },
      { text: "Decide NOW how much data you need before calling it", tag: "Threshold" },
      { text: "Let it run. Do not stop it early because it looks bad on day 2", tag: "Running" },
      { text: "At the threshold, decide: keep it, kill it, or run it again", tag: "Decision" },
      { text: "Write down the result, INCLUDING when it failed", tag: "Logged" },
    ],
    finishedWhen: "Result is in the test log, including the failures. Failures stop us repeating them.",
  },
  P9: {
    title: "Something went wrong — PAUSE FIRST",
    before: [
      "Nothing. Do this the moment you notice",
    ],
    steps: [
      { text: "PAUSING NEVER NEEDS PERMISSION. Restarting does", tag: "Understood" },
      { text: "Money running out → PAUSE before the platform stops it itself", tag: "Paused" },
      { text: "Tracking broken, no conversions showing → PAUSE, fix it, test it again", tag: "Paused" },
      { text: "Landing page down → PAUSE and tell the social team", tag: "Paused" },
      { text: "Spend running away → PAUSE and set a cap. Tell founders and client the SAME DAY", tag: "Paused + capped" },
      { text: "Ads rejected at scale → PAUSE that set, fix against the rules, resubmit", tag: "Paused" },
      { text: "Account restricted → STOP making changes. Do not open a new account. Appeal", tag: "Appealing" },
      { text: "LEADS NOT REACHING THE CRM → DO NOT PAUSE. The money is working and the leads are being lost. Escalate NOW", tag: "Escalated" },
      { text: "In every case the client hears it from US first", tag: "Client told" },
    ],
    finishedWhen: "It is paused or escalated, the client has been told by us, and the cause is written down.",
  },
  P10: {
    title: "Spending more (scaling)",
    before: [
      "Something is working, with enough data to be sure",
    ],
    steps: [
      { text: "Check the result is real, not a good week", tag: "Confirmed" },
      { text: "Ask the client: can they actually handle more leads?", tag: "Client can cope" },
      { text: "Check the numbers still work at the bigger spend", tag: "Maths ok" },
      { stop: "Get written client approval for the higher budget" },
      { stop: "Project lead (if named), then Division Lead, then founders approve the higher budget" },
      { text: "Increase in steps, not in one jump. Big jumps make the platform re-learn", tag: "Ramped" },
      { text: "Watch cost per lead daily for the first week", tag: "Watched" },
    ],
    finishedWhen: "Budget increased in steps with written approval, and being watched daily.",
  },
  P11: {
    title: "Monthly report",
    before: [
      "Full month of data",
      "The change log and the test log",
    ],
    steps: [
      { text: "Pull the numbers: spend, results, cost per result, by channel and campaign", tag: "Numbers" },
      { text: "Check the spend matches what the platforms actually charged", tag: "Reconciled" },
      { text: "Compare with the target and with last month", tag: "Comparison" },
      { text: "Which creatives worked, and why you think so", tag: "Creative insight" },
      { text: "What you tested and what you learned, including failures", tag: "Test results" },
      { text: "Where the money was wasted and what you did about it", tag: "Waste noted" },
      { text: "What you recommend for next month, with a budget", tag: "Next month" },
      { stop: "Division Lead or named project lead checks it before it goes to Shreya. The founders review the master report (card O6)" },
      { text: "Send it to Shreya between the 25th and 28th for the master report. Then present your section on the monthly client call and agree next month there", tag: "With Shreya" },
    ],
    finishedWhen: "Report with Shreya between the 25th and 28th, and next month agreed on the client call.",
  },
  F1: {
    title: "Client setup — BEFORE any spend",
    before: [
      "A new client, or a client where something below is missing",
    ],
    steps: [
      { text: "We have proper access on every platform, plus analytics and the CRM", tag: "Access" },
      { text: "We know their numbers: what a customer is worth, and how many leads become customers", tag: "Economics" },
      { text: "Without those numbers, a cost per lead target is just a guess", tag: "Understood" },
      { text: "It is agreed IN WRITING what counts as a good lead", tag: "Lead defined" },
      { text: "We know how the account is funded and who tops it up", tag: "Funding" },
      { text: "Spend caps are checked, and we know who can approve a budget change", tag: "Caps + approver" },
      { text: "ONE named person at the client approves our work, and we know the ONE channel where their approvals are written down", tag: "Approver named" },
      { text: "The client's dos and don'ts are written down: what we must never say, show or claim", tag: "Dos and don'ts" },
      { text: "IF ANYTHING IS MISSING: tell the Division Lead. It becomes a job with a date. Do not start spending and hope", tag: "Raised" },
    ],
    finishedWhen: "All six exist and are saved. Starting without them means spending money you cannot account for.",
  },
  M1: {
    title: "Paid strategy and the numbers",
    before: [
      "Client's business numbers",
      "Agreed goals",
    ],
    steps: [
      { text: "Get the real numbers: what a deal is worth, the margin, how many leads become customers", tag: "Economics" },
      { text: "Work backwards to what a lead can cost and still make money", tag: "Max CPL" },
      { text: "If the client's target does not survive that maths, SAY SO NOW, not in month three", tag: "Reality check" },
      { text: "Decide which channels, and why each one earns its share of the budget", tag: "Channel mix" },
      { text: "Keep money aside for testing. Without it, testing never happens", tag: "Test budget" },
      { text: "Write the targets with today's numbers written down next to them", tag: "Targets" },
      { stop: "Project lead (if named), then Division Lead, then founders check" },
      { stop: "Client approves the plan and the budget" },
    ],
    finishedWhen: "Approved plan with real numbers behind every target.",
  },
  M2: {
    title: "Audiences and exclusions",
    before: [
      "Tracking proved",
      "Client customer lists where available",
    ],
    steps: [
      { text: "Build audiences by how recently and how strongly people engaged", tag: "Audiences" },
      { text: "Upload the client's customer list so we can exclude them", tag: "Customer list" },
      { text: "Exclude people who already filled the form. On lead campaigns this is pure waste", tag: "Converters excluded" },
      { text: "Exclude current customers and open deals where the client asks", tag: "Exclusions" },
      { text: "Build lookalike audiences from the best list, not just any list", tag: "Lookalikes" },
      { text: "Check the audiences are big enough to actually run", tag: "Size ok" },
    ],
    finishedWhen: "Audiences and exclusions built and saved, and documented in the account notes.",
  },
  M3: {
    title: "Funding and budget control",
    before: [
      "Funding model written down for every account",
    ],
    steps: [
      { text: "For every account, write down: automatic billing, or manual top-up", tag: "Model noted" },
      { text: "For manual accounts, agree at what point we ask for a top-up, and who we ask", tag: "Trigger" },
      { text: "Check the runway every single day on manual accounts", tag: "Daily check" },
      { text: "When it runs low, send a clear message with the exact amount and the date it runs out", tag: "Request sent" },
      { text: "If it is not topped up, PAUSE before the platform stops it. A controlled pause is safer", tag: "Paused safely" },
      { text: "Never let the account be stopped by the platform for non-payment", tag: "Never stopped" },
      { text: "Any budget change above the agreed limit needs written client approval", tag: "Approval" },
    ],
    finishedWhen: "Every account has a funding model, a top-up trigger, and a daily check where it is manual.",
  },
  M4: {
    title: "Tell the social team what the landing page needs",
    before: [
      "Approved campaign plan",
    ],
    steps: [
      { text: "Write what the page must say so it matches the ad. A mismatch wastes every click", tag: "Message match" },
      { text: "Write what the form must ask, and nothing more", tag: "Form fields" },
      { text: "Say where the lead must go and confirm it with Growth Systems", tag: "Lead route" },
      { text: "Send the requirement through your Division Lead with enough notice", tag: "Sent" },
      { stop: "Social and Creative confirms the page passed their checks. No ads point at it before this" },
    ],
    finishedWhen: "Requirements sent, and the page confirmed live and checked before any spend.",
  },
  M5: {
    title: "Monthly account clean-up",
    before: [
      "Live accounts",
    ],
    steps: [
      { text: "Check the spend caps are still set", tag: "Caps ok" },
      { text: "Check naming is still being followed", tag: "Naming ok" },
      { text: "Check who has access and remove anyone who left", tag: "Access clean" },
      { text: "Re-test the tracking with a real lead", tag: "Tracking ok" },
      { text: "Turn off campaigns and ad sets nobody is using", tag: "Cleaned" },
      { text: "Check the landing pages are all still live", tag: "Pages ok" },
    ],
    finishedWhen: "Clean-up done on every live account, and anything found is logged.",
  },
  M6: {
    title: "Handing an ad account back",
    before: [
      "Client is leaving, or moving the work elsewhere",
    ],
    steps: [
      { text: "Confirm what the client owns: the ad account, the audiences, the data", tag: "Ownership" },
      { text: "Export the historical performance data for them", tag: "Data export" },
      { text: "Hand over admin access properly, do not just remove ourselves", tag: "Access handed" },
      { text: "Remove our access AFTER they confirm they have theirs", tag: "Our access removed" },
      { text: "Write a short note on how the account is set up so the next person can pick it up", tag: "Handover note" },
      { stop: "Founders approve the handover" },
    ],
    finishedWhen: "Client has full access and a handover note. We have removed ours.",
  },
};
